import { Component, type ErrorInfo, type ReactNode } from "react";
import { recordClientError } from "@/lib/client-error-reporting";

type Props = { children: ReactNode; boundary?: string };
type State = { error: Error | null; componentStack: string | null };

/**
 * Top-level boundary. It sits above the router provider so it also catches
 * TanStack Router hydration crashes (e.g. React.use() in the hydration
 * <Await>), which route-level errorComponents never see.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    recordClientError(error, {
      source: "error-boundary",
      boundary: this.props.boundary ?? "app_root",
      ...(info.componentStack ? { componentStack: info.componentStack } : {}),
    });
  }

  private retry = () => {
    this.setState({ error: null, componentStack: null });
    if (typeof window !== "undefined") window.location.reload();
  };

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-5 py-16">
        <div className="surface-premium w-full max-w-lg overflow-hidden">
          <div className="panel-royal px-7 py-8 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-80">Shields</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight">We hit a snag loading this</h1>
            <p className="mt-2 text-sm opacity-90">
              Your booking data is safe. Reloading usually clears it straight away.
            </p>
          </div>
          <div className="space-y-5 px-7 py-7">
            <div className="flex flex-wrap justify-center gap-3">
              <button type="button" onClick={this.retry} className="btn-glow">
                Try again
              </button>
              <a href="/" className="btn-secondary-hero">
                Go home
              </a>
            </div>
            <p className="text-center text-xs text-muted-foreground">If this keeps happening, contact Shields support and tell us which page you were using.</p>
          </div>
        </div>
      </div>
    );
  }
}
